import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {RootState} from '../../../../setup'
import {KTSVG} from '../../../../_metronic/helpers'
import * as ProductAction from '../redux/ProductAction'
import {ProductAdd} from './ProductAdd'
import {ProductEdit} from './ProductEdit'
import {ProductDelete} from './ProductDelete'
import {
  ProductModel,
  ProductInitValues,
  ProductSearchModelInitValues,
  ProductTableModelInitValues,
} from '../models/ProductModel'
import {formatDateTime, formatNumber} from '../../../helper'
import {Route, Breadcrumbs} from '../../../components/Breadcrumbs'
import {Can} from '../../../rbac/Can'
import {Field, Form, Formik} from 'formik'
import * as Yup from 'yup'
import 'rsuite/dist/rsuite.css'
import {Link} from 'react-router-dom'
import {Table} from '../../../components/ReactTable'

type Props = {
  className: string
}

export const defaultColDef = {
  resizable: true,
  initialWidth: 100,
  filter: true,
  floatingFilter: true,
  suppressMovable: true,
  suppressKeyboardEvent: (params: any) => params.editing,
}

const ProductTable: React.FC<Props> = ({className}) => {
  const dispatch = useDispatch()
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const [productToEdit, setProductToEdit] = useState<ProductModel>(ProductInitValues)
  const [productToDelete, setProductToDelete] = useState<ProductModel>(ProductInitValues)

  const pages: any = useSelector<RootState>(
    ({products}) => products.pagination?.pages,
    shallowEqual
  )

  const [searchParams, setSearchParams]: any = useState({})

  const [show, setShow] = useState(false)
  const filterHandel = () => {
    setShow(!show)
  }

  const fetchData = useCallback(
    ({pageSize, pageIndex, params, sortBy}: any) => {
      const queryParams = {
        page: pageIndex + 1,
        sortBy: sortBy[0]?.id || 'createdAt',
        sortOrder: sortBy[0]?.desc === false ? 'asc' : 'desc',
        search: params,
      }
      dispatch(ProductAction.findAll(queryParams))
    },
    [searchParams]
  )
  useEffect(() => {
    dispatch(ProductAction.findAll(ProductTableModelInitValues))
  }, [dispatch])

  const loading: any = useSelector<RootState>(({products}) => products.loading, shallowEqual)

  const products: any = useSelector<RootState>((state) => state.products.products, shallowEqual)
  const columns = useMemo(
    () => [
      {
        Header: 'Created Date',
        accessor: 'createdAt',
        Cell: (cellInfo: any) => {
          if (cellInfo?.row?.original?.createdAt) {
            return formatDateTime(cellInfo?.row?.original?.createdAt)
          } else {
            return '-'
          }
        },
        sortable: true,
      },
      {
        Header: 'Updated Date',
        accessor: 'updatedAt',
        Cell: (cellInfo: any) => {
          if (cellInfo?.row?.original?.updatedAt) {
            return formatDateTime(cellInfo?.row?.original?.updatedAt)
          } else {
            return '-'
          }
        },
        sortable: true,
      },
      {Header: 'Name', accessor: 'name'},
      {Header: 'Code', accessor: 'code'},
      {
        Header: 'Quantity In Stock',
        accessor: 'quantityInStock',
        Cell: (cellInfo: any) => {
          if (cellInfo?.row?.original?.quantityInStock) {
            return formatNumber(cellInfo?.row?.original?.quantityInStock)
          } else {
            return '0.00'
          }
        },
      },
      {
        Header: 'Category',
        accessor: 'category.name',
        disableSortBy: true,
        Cell: (params: any) => {
          if (!params?.row?.original?.category) return '-'
          return params?.row?.original?.category.name
        },
      },
      {
        Header: 'UOM',
        accessor: 'UOM',
      },
      {
        Header: 'Actions',

        Cell: (params: any) => (
          <div className='action-btn-wrapper d-flex flex-shrink-0'>
            <Can do='update' on='products'>
              <button
                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                title='Edit Product'
                onClick={(e) => {
                  const product = {...params?.row?.original}
                  product.category = product.category._id
                  setProductToEdit(product)
                  setShowEditDialog(true)
                }}
              >
                <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
              </button>
            </Can>
            <Can do='delete' on='products'>
              <button
                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm '
                data-bs-toggle='modal'
                data-bs-target='#kt_modal_delete_app'
                title='Delete Product'
                onClick={() => {
                  setProductToDelete(params?.row?.original)
                  setShowDeleteDialog(true)
                }}
              >
                <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
              </button>
            </Can>
          </div>
        ),
      },
    ],
    []
  )

  const routes: Route[] = [
    {name: 'Dashboard', url: '/dashboard'},
    {name: 'Products List', url: '/products'},
  ]

  return (
    <>
      <ProductAdd
        show={showAddDialog}
        handleClose={() => {
          setShowAddDialog(false)
        }}
      />
      <ProductEdit
        show={showEditDialog}
        handleClose={() => {
          setShowEditDialog(false)
        }}
        product={productToEdit}
      />

      <ProductDelete
        show={showDeleteDialog}
        handleClose={() => {
          setShowDeleteDialog(false)
        }}
        product={productToDelete}
      />

      <div className={`card card-custom ${className}`}>
        <Breadcrumbs routes={routes} />
        <div className='card-header border-0 pt-5'>
          <h1 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Products List</span>
          </h1>
          <Can do='create' on='products'>
            <div
              className='card-toolbar'
              data-bs-toggle='tooltip'
              data-bs-placement='top'
              data-bs-trigger='hover'
              title='Add Product'
            >
              <a
                href='#'
                className='btn btn-sm btn-light-primary'
                data-bs-toggle='modal'
                data-bs-target='#kt_modal_create_app'
                id='kt_toolbar_primary_button'
                onClick={() => {
                  setShowAddDialog(true)
                }}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                Add Product
              </a>
            </div>
          </Can>
        </div>

        <div className='card-body'>
          <div
            className={`d-flex align-items-center ${
              !show ? 'justify-content-end' : 'justify-content-between'
            }`}
          >
            <p className='my-0 me-3' style={{color: '#004B87', fontSize: '14px'}}>
              <b>Search Filters</b>
            </p>
            <span onClick={filterHandel}>
              <img src='/media/svg/icons/General/filter.svg' alt='' />
            </span>
          </div>
          {show ? (
            <Formik
              initialValues={ProductSearchModelInitValues}
              validationSchema={Yup.object({
                name: Yup.string(),
              })}
              onSubmit={(values: any) => {
                setSearchParams(values)
              }}
            >
              {({values, setFieldValue}) => (
                <Form>
                  <div className='row row-cols-lg-2 row-cols-md-2 row-cols-1 g-5'>
                    <div className='form-group col'>
                      <label htmlFor='number' className='fw-bold'>
                        Name
                      </label>
                      <Field
                        name='name'
                        placeholder='Name'
                        className='form-control form-control-lg'
                      />
                    </div>
                  </div>

                  <div className='d-flex justify-content-end mt-10'>
                    <Link
                      to='/products'
                      className='btn btn-sm btn-light-primary me-3'
                      data-bs-toggle='tooltip'
                      data-bs-placement='top'
                      title='Cancel'
                      onClick={() => {
                        setSearchParams(ProductSearchModelInitValues)
                        setFieldValue('name', '')
                      }}
                    >
                      <span className='indicator-label'>Cancel</span>
                    </Link>

                    <button
                      type='submit'
                      className='btn btn-sm btn-primary'
                      data-bs-toggle='tooltip'
                      data-bs-placement='top'
                      title='Search'
                      id='liveToastBtn'
                    >
                      <span className='indicator-label'>Search</span>
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          ) : null}
          <div className='d-flex mt-5 '>
            <div className='react-table-wrapper' style={{width: '75%'}}>
              <Table
                columns={columns}
                data={products}
                params={searchParams}
                pageCount={pages}
                fetchData={fetchData}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export {ProductTable}
